import React, {memo} from 'react';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import Icon from '@/components/atoms/Icon';
import ArrowButton from '@/components/atoms/ArrowButton';
import styles from './EngagementModels.module.scss';

interface EngagementCardData {
    title: string;
    /** Headline figure, e.g. `From $15,000`. */
    price: string;
    /** Small suffix on the price's baseline, e.g. `/hr`. Omit for a flat figure. */
    priceUnit?: string;
    /** Qualifying line under the price. */
    priceNote: string;
    description: string;
    bestFor: string[];
    /** Raises one card as the recommended model. */
    featured?: boolean;
}

export interface EngagementModelsProps {
    heading: string;
    subheading?: string;
    cards: EngagementCardData[];
    trustBadges?: string[];
    /** Primary CTA under the trust badges. Omit either field to hide the button. */
    ctaText?: string;
    ctaUrl?: string;
    /** Makes the CTA a button that runs this (e.g. opens the enquiry form) instead of a link; `ctaUrl` is then unused. */
    onCtaClick?: () => void;
}

// Plan-card layout: centred title, price up top, then the feature list. The featured card
// is only lifted and given a deeper shadow, so at rest all three cards look alike.
const EngagementCard = memo(({card}: {card: EngagementCardData}) => (
    <div className={`${styles.card} ${card.featured ? styles.cardFeatured : ''}`}>
        <div className={`engagement-divider ${styles.cardHeader}`}>
            <h3 className={`font-headers engagement-text ${styles.text} ${styles.cardTitle}`}>{card.title}</h3>

            <div className={styles.priceRow}>
                <span className={`font-headers engagement-text ${styles.text} ${styles.price}`}>{card.price}</span>
                {card.priceUnit && (
                    <span className={`font-regular-text engagement-text-muted ${styles.textMuted} ${styles.priceUnit}`}>
                        {card.priceUnit}
                    </span>
                )}
            </div>

            <p className={`font-regular-text engagement-chip ${styles.chip}`}>{card.priceNote}</p>
        </div>

        <p className={`font-regular-text engagement-text ${styles.text} ${styles.cardDesc}`}>{card.description}</p>

        <div className={styles.featureList}>
            {card.bestFor.map((item) => (
                <div key={item} className={styles.featureRow}>
                    <Icon name="Check" className={`engagement-check ${styles.check}`} />
                    <span className={`font-regular-text engagement-text ${styles.text} ${styles.featureText}`}>{item}</span>
                </div>
            ))}
        </div>
    </div>
));
EngagementCard.displayName = 'EngagementCard';

const EngagementModels = ({heading, subheading, cards, trustBadges, ctaText, ctaUrl, onCtaClick}: EngagementModelsProps) => (
    <section className="top-space mobile-top-padding bottom-spacing mobile-padding-bottom">
        <Container>
            <div className={styles.header}>
                <h2 className={`font-headers ${styles.heading}`}>{heading}</h2>
                {subheading && <p className={`font-regular-text ${styles.subheading}`}>{parse(subheading)}</p>}
            </div>

            <div className={styles.cardsRowWrap}>
                <div className={styles.grid}>
                    {cards.map((card, i) => (
                        <div className={styles.gridItem} key={i}>
                            <EngagementCard card={card} />
                        </div>
                    ))}
                </div>
            </div>

            {trustBadges && trustBadges.length > 0 && (
                <div className={styles.trustBadgeRow}>
                    {trustBadges.map((badge, i) => (
                        <div key={i} className={`font-headers ${styles.trustBadgePill}`}>
                            <Icon name="Check" className={`engagement-badge-check ${styles.trustBadgeCheck}`} />
                            {badge}
                        </div>
                    ))}
                </div>
            )}

            {ctaText && onCtaClick && (
                <div className={styles.ctaRow}>
                    <ArrowButton variant="primary" onClick={onCtaClick}>
                        {ctaText}
                    </ArrowButton>
                </div>
            )}

            {ctaText && ctaUrl && !onCtaClick && (
                <div className={styles.ctaRow}>
                    <ArrowButton variant="primary" href={ctaUrl} target="_blank">
                        {ctaText}
                    </ArrowButton>
                </div>
            )}
        </Container>
    </section>
);

export default memo(EngagementModels);
